import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { IColumnFormatting, IColumnMetaData, IReportData, IReportMetadata, IReportResponse, ReportDataService } from '@app/shared/report/report-data.service';
import { MaxWidth, ResponsiveService } from '@app/shared/responsive/responsive.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { IUserData } from '@app/shared/services/auth/auth-data.service';
import _ from 'underscore';
import { ValueGetterParams } from 'ag-grid-community';
import { IEmbeddedReportGridReady } from '@app/shared/report/embedded-report/embedded-report.component';
import { StateService, Transition, UiOnParamsChanged } from '@uirouter/angular';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { ReportGridService } from '@app/shared/report/report-grid.service';
import { IObject } from '@type_definitions/datatypes';
import { DateType } from '@app/shared/services/date.util';
import { GaStateService } from '@app/shared/services/ga-state.service';
import { ChartService, IChartReport } from './chart.service';
import { util } from '@app/shared/util';
import { CurrencyISO, CurrencyService } from '@app/shared/services/currency.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { DialogSize, GaMessageService } from '@app/shared/message/ga-message.service';
import { FullChartDialogComponent } from '@app/main/report/chart/full-chart-dialog.component';

export enum Chart {
  CUSTOMER = 'customer',
  BP = 'bp'
}

export interface IChartReportModel {
  orderDate_FROM:Date|string;
  orderDate_TO:Date|string;
  webshop?:number;
  customer?:number;
  bp?:string[];
  currency?:string;
  bpGroup?:number;
  testOrder?:string;
}

export const CHART_REPORT_ID:number = 271;
export const CANCEL_REPORT_ID:string = 'cancelChart-271';

@Injectable()
export abstract class BaseChartComponent implements OnInit, UiOnParamsChanged, OnDestroy {
  public chartReportId:number = CHART_REPORT_ID;
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[] = [];
  public model:IChartReportModel = <any>{};
  public loading:boolean;
  public chartReports:IChartReport[];
  public reportData:IReportData;

  public abstract chartOnInit():void;
  public abstract fillFields():void;
  public abstract createReports():void;
  public abstract paramsEqual(p1:IObject, p2:IObject):boolean;
  public abstract modelToParams():IObject;
  public abstract paramsToModel(params:any):IChartReportModel;
  public abstract validateFilters(doNotShowValidationErrors?:boolean):boolean;

  protected reportMetaData:IReportMetadata;
  protected currentScreen:string;
  protected defaultCurrency:string;
  protected defaultFromDate:string = DateType.FIRST_OF_PREVIOUS_YEAR;
  protected labelClass:string = 'col-md-2';
  protected valueClass:string = 'col-md-4';
  protected fullWidthClass:string = 'col-sm-12';

  protected constructor(
    protected responsiveService:ResponsiveService,
    protected authService:AuthService,
    protected gaStateService:GaStateService,
    protected formlyService:GaFormlyService,
    protected reportFiltersUtil:ReportFiltersUtil,
    protected reportDataService:ReportDataService,
    protected stateService:StateService,
    protected chartService:ChartService,
    protected reportGridService:ReportGridService,
    protected currencyService:CurrencyService,
    protected formattingService:FormattingService,
    protected messageService:GaMessageService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.responsiveService.width = MaxWidth.LG;
    const currentUser:IUserData = this.authService.getCurrentUser();
    const userCurrency:string = currentUser.location?.companyCountry?.country?.currency;

    this.defaultCurrency = userCurrency === CurrencyISO.EUR? CurrencyISO.EUR : CurrencyISO.USD;
    this.currentScreen = this.gaStateService.screen.toUpperCase();

    this.chartOnInit();
    this.load();
  }

  private load():void {
    this.loadMetaData()
      .then(() => {
        this.fillFields();
      })
      .then(() => {
        this.startLoad(true);
      });
  }

  public uiOnParamsChanged = (_newValues:any, transition:Transition):void => {
    const to:IObject = transition.params<IObject>('to');
    this.model = this.paramsToModel(to);

    // when all 'to' parameters are empty, it signifies that the object represents the default state, typically when a user revisits a view -> do not
    this.startLoad(util.isObjectEmpty(to));
  };

  private async loadMetaData():Promise<void> {
    return this.reportDataService.loadMetaData(CHART_REPORT_ID)
      .then((data:IReportMetadata) => {
        this.reportMetaData = data;
      })
      .then(() => {
        this.createReports();
      });
  }

  protected onModelChange = ():void => {
    _.forEach(this.chartReports, (report:IChartReport) => {
      if(report.params) {
        report.params = this.createReportParams();
      }
    });
  };

  protected getReportFilters():IObject {
    return {
      Customer: this.model.customer,
      Webshop: this.model.webshop,
      Bp: this.model.bp,
      Currency: this.model.currency,
      Order_date_FROM: this.model.orderDate_FROM,
      Order_date_TO: this.model.orderDate_TO,
      Bp_group: this.model.bpGroup,
      Test_order: this.model.testOrder
    };
  }

  public startLoad = (doNotShowValidationErrors?:boolean):void => {
    if (this.validateFilters(doNotShowValidationErrors)) {
      const params:IObject = this.modelToParams();
      if (!this.paramsEqual(params, this.stateService.params)) {
        this.stateService.go(this.stateService.$current.name, params);
      } else {
        this.loadReportData();
      }
    }
  };

  public stopLoad():void {
    this.reportDataService.cancelLoadData(CANCEL_REPORT_ID);
  }
  
  protected getSectionTitle(sectionId:string):string {
    return `${this.currentScreen}.${sectionId}.TITLE`;
  }

  public onChangeCollapsed(collapsed:boolean, chartReport:IChartReport):void {
    // because the dashboard section is collapsed by default, ag-grid is not calculating the correct width for grouped columns
    // this fixes it by resizing the grouped columns of the report when the section is expanded
    if(!collapsed) {
      setTimeout(() => this.reportGridService.autoSizeGroupedColumns(chartReport.id));
    }
  }

  private loadReportData = ():void => {
    this.loading = true;
    let reportFilters = this.getReportFilters();
    reportFilters = this.reportFiltersUtil.getQueryFiltersFromModel(reportFilters, this.reportMetaData, {
      applyDefaultValues: true,
      applyNullValues: true
    });
    this.reportDataService.loadData(CANCEL_REPORT_ID, CHART_REPORT_ID, reportFilters)
      .then((reportData:IReportResponse) => {
        this.reportData = reportData.data;
      })
      .finally(() => {
        this.loading = false;
      });
  };

  public onGridReady(event:IEmbeddedReportGridReady):void {
    this.chartService.chartInit(event);
  }

  protected createReportParams():IObject {
    return {
      reportId: CHART_REPORT_ID,
      filters: this.reportFiltersUtil.filtersToParam(this.getReportFilters()),
      showfilters: true,
      showcomments: true
    };
  }

  protected modifyColumn(name:string, customMetadata:Partial<IColumnMetaData>, customFormatting?:IColumnFormatting):IColumnMetaData {
    const column:IColumnMetaData = _.findWhere(this.reportMetaData.columns, { name });
    if (customFormatting) {
      customMetadata = _.extend(customMetadata, { formatting: _.extend({}, column.formatting, customFormatting) });
    }
    customMetadata.id = customMetadata.id || name;
    return _.extend({}, column, customMetadata);
  }

  protected createDateReport():IChartReport {
    const columns:IColumnMetaData[] = [
      this.modifyColumn('Order_date', {
        id: 'Year',
        listSequence: 1,
        label: 'Year',
        groupingSequence: 1,
        screenType: 'YEAR',
        type: 'TEXT'
      }, { defaultinlist: false }),
      this.modifyColumn('Order_date', {
        id: 'Quarter',
        listSequence: 2,
        label: 'Quarter',
        groupingSequence: 2,
        screenType: 'QUARTER',
        type: 'TEXT'
      }, { defaultinlist: false }),
      this.modifyColumn('Total_price', {
        listSequence: 3,
        aggregateOn: 'sum',
        label: 'Spend'
      }),
      this.modifyColumn('Order_number', { listSequence: 4 }),
      this.modifyColumn('Status', { listSequence: 5 }),
      this.modifyColumn('Order_date', { listSequence: 6 }),
      this.modifyColumn('Webshop', { listSequence: 7 }),
      this.modifyColumn('Geography', { listSequence: 8 }, { defaultinlist: false }),
      this.modifyColumn('Country', { listSequence: 9 }),
      this.modifyColumn('Part_number', { listSequence: 10 }),
      this.modifyColumn('Category', { listSequence: 11 }),
      this.modifyColumn('Vendor', { listSequence: 12 }),
      this.modifyColumn('Bp', { listSequence: 13 }),
      this.modifyColumn('Description', { listSequence: 14 }),
      this.modifyColumn('Quantity', { listSequence: 15 }),
      this.modifyColumn('Item_price', { listSequence: 16 })
    ];

    return {
      metadata: _.extend({}, this.reportMetaData, { columns })
    };
  }

  protected createCountryReport():IChartReport {
    const columns = [
      this.modifyColumn('Total_price', {
        listSequence: 1,
        aggregateOn: 'sum',
        label: 'Spend'
      }, { headerdropdown: true }),
      this.modifyColumn('Order_number', { listSequence: 2 }),
      this.modifyColumn('Status', { listSequence: 3 }),
      this.modifyColumn('Order_date', { listSequence: 4 }),
      this.modifyColumn('Webshop', { listSequence: 5 }),
      this.modifyColumn('Geography', { listSequence: 6 }, { defaultinlist: false }),
      this.modifyColumn('Country', {
        listSequence: 7,
        groupingSequence: 1
      }, { defaultinlist: false }),
      this.modifyColumn('Part_number', { listSequence: 8 }),
      this.modifyColumn('Category', { listSequence: 9 }),
      this.modifyColumn('Vendor', { listSequence: 10 }),
      this.modifyColumn('Bp', { listSequence: 11 }),
      this.modifyColumn('Description', { listSequence: 12 }),
      this.modifyColumn('Quantity', { listSequence: 13 }),
      this.modifyColumn('Item_price', { listSequence: 14 })
    ];

    return {
      metadata: _.extend({}, this.reportMetaData, { columns }),
      sort: [
        {
          name: 'Total_price',
          direction: 'desc'
        }
      ]
    };
  }

  protected createCustomerReport():IChartReport {
    const columns = [
      this.modifyColumn('Total_price', {
        listSequence: 1,
        aggregateOn: 'sum',
        label: 'Revenue'
      }, { headerdropdown: true }),
      this.modifyColumn('Order_number', { listSequence: 2 }),
      this.modifyColumn('Status', { listSequence: 3 }),
      this.modifyColumn('Order_date', { listSequence: 4 }),
      this.modifyColumn('Webshop', { listSequence: 5 }),
      this.modifyColumn('Geography', { listSequence: 6 }, { defaultinlist: false }),
      this.modifyColumn('Country', { listSequence: 7 }),
      this.modifyColumn('Customer', {
        listSequence: 8,
        groupingSequence: 1
      }, { defaultinlist: false }),
      this.modifyColumn('Part_number', { listSequence: 9 }),
      this.modifyColumn('Category', { listSequence: 10 }),
      this.modifyColumn('Vendor', { listSequence: 11 }),
      this.modifyColumn('Description', { listSequence: 12 }),
      this.modifyColumn('Quantity', { listSequence: 13 }),
      this.modifyColumn('Item_price', { listSequence: 14 })
    ];

    return {
      metadata: _.extend({}, this.reportMetaData, { columns }),
      sort: [
        {
          name: 'Total_price',
          direction: 'desc'
        }
      ]
    };
  }

  protected createCategoryReport():IChartReport {
    const columns = [
      this.modifyColumn('Total_price', { listSequence: 1, label: 'Revenue' }),
      this.modifyColumn('Order_number', { listSequence: 2 }),
      this.modifyColumn('Status', { listSequence: 3 }),
      this.modifyColumn('Order_date', { listSequence: 4 }),
      this.modifyColumn('Webshop', { listSequence: 5 }),
      this.modifyColumn('Geography', { listSequence: 6 }, { defaultinlist: false }),
      this.modifyColumn('Country', { listSequence: 7 }),
      this.modifyColumn('Part_number', { listSequence: 8 }),
      this.modifyColumn('Category', {
        listSequence: 9,
        groupingSequence: 1
      }),
      this.modifyColumn('Vendor', { listSequence: 10 }),
      this.modifyColumn('Bp', { listSequence: 11 }),
      this.modifyColumn('Description', { listSequence: 12 }),
      this.modifyColumn('Quantity', {
        listSequence: 13,
        aggregateOn: 'sum'
      }),
      this.modifyColumn('Item_price', { listSequence: 14 }),
      // custom column to simplify order status
      {
        id: 'Close_status',
        label: 'Close Status',
        listSequence: 15,
        pivotSequence: 1,
        type: 'T',
        formatting: {
          headerdropdown: true,
          showinlist: true
        },
        valueGetter: (params:ValueGetterParams) => {
          return params.getValue('Status') === 'CLOSED'? 'CLOSED' : 'OPEN';
        }
      }
    ];

    return {
      metadata: _.extend({}, this.reportMetaData, { columns })
    };
  }
  
  protected createMoMRevenueReport():IChartReport {
    const columns:IColumnMetaData[] = [
      this.modifyColumn('Order_date', {
        id: 'Year',
        listSequence: 1,
        label: 'Year',
        pivotSequence: 1,
        screenType: 'YEAR',
        type: 'TEXT'
      }, { defaultinlist: false, headerdropdown: true }),
      this.modifyColumn('Order_date', {
        id: 'Month',
        listSequence: 2,
        label: 'Month',
        groupingSequence: 1,
        screenType: 'MONTH',
        type: 'TEXT'
      }, { defaultinlist: false, headerdropdown: true }),
      this.modifyColumn('Total_price', {
        listSequence: 3,
        aggregateOn: 'sum',
        label: 'Spend'
      }),
      this.modifyColumn('Order_number', { listSequence: 4 }),
      this.modifyColumn('Status', { listSequence: 5 }),
      this.modifyColumn('Order_date', { listSequence: 6 }),
      this.modifyColumn('Webshop', { listSequence: 7 }),
      this.modifyColumn('Geography', { listSequence: 8 }, { defaultinlist: false }),
      this.modifyColumn('Country', { listSequence: 9 }),
      this.modifyColumn('Part_number', { listSequence: 10 }),
      this.modifyColumn('Category', { listSequence: 11 }),
      this.modifyColumn('Vendor', { listSequence: 12 }),
      this.modifyColumn('Bp', { listSequence: 13 }),
      this.modifyColumn('Description', { listSequence: 14 }),
      this.modifyColumn('Quantity', { listSequence: 15 }),
      this.modifyColumn('Item_price', { listSequence: 16 })
    ];

    return {
      metadata: _.extend({}, this.reportMetaData, { columns })
    };
  }

  protected createOrderDateFields():IExtraFieldConfigurationObject {
    return this.reportFiltersUtil.createDateRangeFilterFields(this.formlyService,
      undefined, 'orderDate', this.onModelChange, this.labelClass, this.valueClass, this.fullWidthClass);
  }

  protected createCurrencyField():IExtraFieldConfigurationObject {
    return this.formlyService.aSelect('currency', 'GA.GENERAL.CURRENCY')
      .withoutEditable()
      .withLabelClass(this.labelClass)
      .withValueClass(this.valueClass)
      .isRequired()
      .withLabelProp('value')
      .withValueProp('id')
      .withOptions(this.currencyService.getMainCurrencies())
      .onChange(this.onModelChange)
      .build();
  }
  
  public onEnlargeChart(chartReport:IChartReport):void {
    this.messageService.showDialog({
      wrapperComponent: false,
      component: FullChartDialogComponent,
      inputs: {
        gridId: chartReport.id,
        title: chartReport.title,
        currency: this.model.currency
      },
      size: DialogSize.FULL
    });
  }

  public ngOnDestroy():void {
    this.responsiveService.width = MaxWidth.SM;
  }
}